import React from "react";
import { Search, Settings, FileText, ScanLine, Share2, Camera } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { PermissionKey } from "../types/permissions";

/**
 * Presentation + routing layer for page features. This is the one place that
 * maps a `page.*` permission key to its route(s) and nav entry, so App.tsx and
 * Navbar.jsx are fully data-driven — adding a screen means adding one entry
 * here (plus its key/label in ../types/permissions.ts).
 *
 * Kept separate from ../types/permissions.ts because it needs React, lazy
 * imports and lucide icons, which that pure-data module must not pull in.
 */

// Lazy-loaded page components (moved here from App.tsx).
const Configurations = React.lazy(() => import("../pages/Configurations"));
const ConfigureStages = React.lazy(() => import("../pages/ConfigureStages"));
const ConfigurePaymentMethods = React.lazy(() => import("../pages/ConfigurePaymentMethods"));
const ConfigureUsers = React.lazy(() => import("../pages/ConfigureUsers"));
const ConfigureRoles = React.lazy(() => import("../pages/ConfigureRoles"));
const ReportsLanding = React.lazy(() => import("../pages/ReportsLanding"));
const ReportsLocationPage = React.lazy(() => import("../pages/reports/location"));
const ReportsRequestsPage = React.lazy(() => import("../pages/reports/requests"));
const RequestsDashboard = React.lazy(() => import("../pages/reports/requests/RequestsDashboard"));
const ReportsQuotesPage = React.lazy(() => import("../pages/reports/quotes"));
const ReportsNoPhotosPage = React.lazy(() => import("../pages/reports/no-photos"));
const ReportsNoReconLocationPage = React.lazy(() => import("../pages/reports/no-recon-location"));
const ReportsInventoryFilterPage = React.lazy(() => import("../pages/reports/inventory-filter"));
const ReportsKbbLendingPage = React.lazy(() => import("../pages/reports/kbb-lending"));
const UserActivityReportPage = React.lazy(() => import("../pages/reports/user-activity"));
const AllSavedReportsPage = React.lazy(() => import("../pages/reports/AllSavedReportsPage"));
const AutoInvoicePage = React.lazy(() => import("../pages/auto-invoice"));
const SyndicationPage = React.lazy(() => import("../pages/syndication"));
const ImageProcessing = React.lazy(() => import("../pages/ImageProcessing"));
// "AI Image Inspection" route is disabled (see App.tsx history) — kept as
// ReportDisabled under the reports feature.
import ReportDisabled from "../pages/reports/ReportDisabled";

export interface NavMeta {
  label: string;
  icon: LucideIcon;
  path: string;
}

export interface RouteDef {
  path: string;
  element: React.ReactNode;
}

export interface PageFeatureUI {
  perm: PermissionKey;
  minimum: "view" | "edit";
  /** Present => the feature shows a nav item (gated by `can(perm, minimum)`). */
  nav?: NavMeta;
  /** Route(s) gated behind `perm`. Empty => nav-only (route lives elsewhere). */
  routes: RouteDef[];
}

export const PAGE_FEATURES_UI: PageFeatureUI[] = [
  {
    // Nav-only: /search-vehicle stays hardcoded + ungated in App.tsx because it
    // is the default RequirePermission redirect target (gating it would loop).
    perm: "page.vehicle_search",
    minimum: "view",
    nav: { label: "Vehicle search", icon: Search, path: "/search-vehicle" },
    routes: [],
  },
  {
    perm: "page.scan_invoice",
    minimum: "view",
    nav: { label: "Scan Invoice", icon: ScanLine, path: "/auto-invoice" },
    routes: [{ path: "/auto-invoice", element: <AutoInvoicePage /> }],
  },
  {
    perm: "page.configurations",
    minimum: "view",
    nav: { label: "Configurations", icon: Settings, path: "/configurations" },
    routes: [
      { path: "/configurations", element: <Configurations /> },
      { path: "/configurations/stages", element: <ConfigureStages /> },
      { path: "/configurations/payment-methods", element: <ConfigurePaymentMethods /> },
      { path: "/configurations/users", element: <ConfigureUsers /> },
      { path: "/configurations/roles", element: <ConfigureRoles /> },
    ],
  },
  {
    perm: "page.reports",
    minimum: "view",
    nav: { label: "Reports", icon: FileText, path: "/reports" },
    routes: [
      { path: "/reports", element: <ReportsLanding /> },
      { path: "/reports/location", element: <ReportsLocationPage /> },
      { path: "/reports/requests", element: <RequestsDashboard /> },
      { path: "/reports/requests/list", element: <ReportsRequestsPage /> },
      { path: "/reports/quotes", element: <ReportsQuotesPage /> },
      { path: "/reports/no-photos", element: <ReportsNoPhotosPage /> },
      { path: "/reports/no-recon-location", element: <ReportsNoReconLocationPage /> },
      { path: "/reports/inventory-filter", element: <ReportsInventoryFilterPage /> },
      { path: "/reports/kbb-lending", element: <ReportsKbbLendingPage /> },
      { path: "/reports/user-activity", element: <UserActivityReportPage /> },
      { path: "/reports/saved", element: <AllSavedReportsPage /> },
      { path: "/reports/image-inspection", element: <ReportDisabled /> },
    ],
  },
  {
    perm: "page.syndication",
    minimum: "view",
    nav: { label: "Manage Syndication", icon: Share2, path: "/syndication" },
    routes: [{ path: "/syndication", element: <SyndicationPage /> }],
  },
  {
    // PROOF: registering the Image Processing feature is one entry here plus one
    // in permissions.ts. The real page lives on the image-processing branch and
    // replaces this placeholder element, reusing the same key.
    perm: "page.image_processing",
    minimum: "view",
    nav: { label: "Image Processing", icon: Camera, path: "/image-processing" },
    routes: [{ path: "/image-processing", element: <ImageProcessing /> }],
  },
];
